import {Component, EventEmitter, Input, OnChanges, OnInit, Output} from '@angular/core';
import {BsModalRef} from "ngx-bootstrap/modal";
import {AlertBannerService, AlertType} from 'src/app/lib/alert-banner';
import { PlanService } from 'src/app/services/plan-status/plan-status.service';
import {SurveyService} from 'src/app/services/survey/survey.service';
import { AiSettingsService, AiSettingsResponse } from 'src/app/services/ai-settings/ai-settings.service';
import { Subscription } from 'rxjs';


@Component({
  selector: 'app-export-settings-modal',
  templateUrl: './export-referee-settings-modal.component.pug',
  styleUrls: ['./export-referee-settings-modal.component.scss'],
  standalone: false
})
export class ExportRefereeSettingsModalComponent implements OnInit, OnChanges {

  @Input() refereeDetails: any;
  @Input() checkId;
  competencies: any;
  modifications: any;
  @Output() reportExportStatus = new EventEmitter();
  disableSubmit = false;
  loaded = false;
  showRefereeModificationCheckbox = true;
  showCoreCompetenciesCheckbox = true;
  refereeReportData: any;
  refereeCompletedStates: Array<string> = ['answered']
  aiSummaryEnabled = false;
  private aiSettingsSubscription?: Subscription;

  submitting = false;

  model: any = {
    ai_summary: true,
    coverage: true,
    sentiment: true,
    turnaround_time: true,
    word_count: true,
    candidate_details: true,
    candidate_responses: true,
    referee_details: true,
    referee_modifications: true,
    core_competencies: true,
  };

  refereeModel: Array<any> = []

  constructor(public _bsModalRef: BsModalRef,
              private alertList: AlertBannerService,
              private planservice: PlanService,
              private surveyService: SurveyService,
              private aiSettingsService: AiSettingsService) {
  }

  async ngOnInit(): Promise<void> {
    // Subscribe to AI settings
    this.aiSettingsSubscription = this.aiSettingsService.aiSettings$.subscribe({
      next: (settings: AiSettingsResponse | null) => {
        this.aiSummaryEnabled = settings?.config?.summary ?? false;
      },
      error: (error) => {
        console.error('Error loading AI settings for export modal:', error);
        this.aiSummaryEnabled = false;
      }
    });
    // Initial load
    this.aiSettingsService.getAiSettings().subscribe();
    this.loaded = true;
    let showModification = false;
    let showCompetencies = false;
    if (this.refereeDetails?.length > 0) {
      let promiseArray: Array<any> = [];
      this.refereeDetails.forEach(async (referee: any) => {
        if (this.refereeCompletedStates.includes(referee.status)){
          // Making call to get report to return referee modification data
          let refereeReportData = this.surveyService.getReportPromise(this.checkId, referee.id).toPromise();
          promiseArray.push(refereeReportData);
        }
      });
      await Promise.all(promiseArray)
      .then(data => {
        data.forEach(apiResponseData => {
          let refereeData = apiResponseData.data.referee;
          refereeData['selected'] = true;
          let refereeObjectFromInput = this.refereeDetails.filter((referee) => referee.id == refereeData.id)[0];
          if (this.refereeCompletedStates.includes(refereeObjectFromInput.status)) this.refereeModel.push(refereeData);
          if (refereeData?.core_competency) {
            showCompetencies = true;
            this.competencies = refereeData.core_competency;
          }
          if (refereeData.modifications && refereeData.modifications?.length > 0) {
            showModification = true;
            this.modifications = refereeData.modifications;
          };
        })
      }).finally(() => {
        if (!showCompetencies) this.model.core_competencies = false;
        if (!showModification) this.model.referee_modifications = false;
        this.showRefereeModification();
        this.showCoreCompetencies();
        this.loaded = false;
      });
    }
  }

  ngOnChanges() {
  }

  submit() {
    this.disableSubmit = true;
    let content = {
      coverage: this.model.coverage,
      sentiment: this.model.sentiment,
      turnaround_time: this.model.turnaround_time,
      word_count: this.model.word_count,
      candidate_details: this.model.candidate_details,
      candidate_responses: this.model.candidate_responses,
      referee_details: this.model.referee_details,
      referee_modifications: this.model.referee_modifications,
      core_competencies: this.model.core_competencies
    }

    if (this.aiSummaryEnabled) {
      content['ai_summary'] = this.model.ai_summary;
    }

    this.loaded = true;
    const exportPromise: Array<Promise<any>> = []
    let selectedRefereeIds : Array<number> = [];
    let allRefereeIds: Array<number> = [];
    this.refereeModel.forEach(
      referee => {
        if (referee.selected){
          exportPromise.push(new Promise((resolve, reject) => {
            this.surveyService.exportPDF(this.checkId, referee.id, content, resolve, reject);
          }));
          selectedRefereeIds.push(parseInt(referee.id));
        }
        allRefereeIds.push(parseInt(referee.id));
      }
    );
    if (exportPromise.length > 0) {
      const metaData = {
        "log":{
          "message": `User clicked on 'Multi Export Report' to export following referee's : ${selectedRefereeIds}
          from list the following list of referee: ${allRefereeIds} for check: ${this.checkId}.`
        },
        "audit_log": "USER_MULTI_EXPORT_REPORT"
      };
      this.planservice.postLog(metaData);
    }
    return Promise.all(exportPromise).then((values) => {
      this.loaded = false;
      this.cancel();
      this.disableSubmit = false;
      this.alertList.append([{
        active: true,
        type: AlertType.SUCCESS,
        message: 'Success! The report has been exported successfully.'
      }]);
      this.reportExportStatus.emit('Success');
    }).catch((values) => {
      this.loaded = false;
      this.cancel();
      this.disableSubmit = false;
      this.alertList.append([{
        active: true,
        type: AlertType.DANGER,
        message: 'Oops! Something went wrong. Our developers have been notified. Feel free to try again.'
      }]);
      this.reportExportStatus.emit('Failure');
    });
  }

  ngOnDestroy(): void {
    if (this.aiSettingsSubscription) {
      this.aiSettingsSubscription.unsubscribe();
    }
  }

  cancel() {
    this._bsModalRef.hide();
  }

  checkboxChange() {
    this.checkToDisableSubmit();
    this.showRefereeModification();
  }

  private checkToDisableSubmit() {
    let numberOfSelectedCheckbox = Object.keys(this.model).filter((val) => {
      return this.model[val] === true
    }).length
    let numberOfSelectedReferee = this.refereeModel.filter(referee => {
      return referee.selected === true
    }).length
    this.disableSubmit = numberOfSelectedReferee < 1;
  }

  /**
   * Show referee modification check box if any one of the selected referee has done modifications
   */
  showRefereeModification() {
    this.showRefereeModificationCheckbox = this.refereeModel.filter((referee: any) => referee.selected && referee.modifications && referee.modifications?.length > 0).length > 0;
  }


  /**
   * Show competencies check box if any one of the selected referee has competencies
   */
   showCoreCompetencies() {
    this.showCoreCompetenciesCheckbox = this.refereeModel.filter((referee: any) => referee.selected && referee?.core_competency).length > 0;
  }
}
